"use client";

import React, { useState, useEffect } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "../common/Table";
import DynamicForm from "./Popup";
import Button from "./Button";
import {
  useGetCityIntroductionDetails,
  useAddCityIntroductionDetails,
  useUpdateCityIntroductionDetails,
  useCityIntroductionStatus,
  useCityIntroductionImageUpload,
  getCityByname,
  useGetAllBookingTypes,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface CityIntroduction {
  id: number;
  city_id: string;
  booking_type: string;
  title: string;
  description: string;
  image_path: string;
  created_by: string;
  status: number; // 0 or 1
  city_name: string;
  package_name: string;
}

interface City {
  city_id: number;
  city_name: string;
  state_name: string;
  country_code: string;
  country_id: number;
  state_id: number;
  latitude: string;
  longitude: string;
  north_east_latitude: string;
  north_east_longitude: string;
  south_west_latitude: string;
  south_west_longitude: string;
  status: number;
}

interface BookingType {
  id: number;
  booking_type: string;
  menu_order: number | null;
  created_date: string;
  created_by: number;
  modified_by: number;
  modified_date: string;
  status: number;
  ip: string | null;
}

// Utility function to construct proper image URLs
const constructImageUrl = (imagePath: string) => {
  if (!imagePath) return "";

  const baseUrl = process.env.NEXT_PUBLIC_B2B_URL || "";

  // Remove leading slash from imagePath if baseUrl already ends with slash
  let cleanImagePath = imagePath;
  if (baseUrl.endsWith("/") && imagePath.startsWith("/")) {
    cleanImagePath = imagePath.substring(1);
  } else if (!baseUrl.endsWith("/") && !imagePath.startsWith("/")) {
    cleanImagePath = "/" + imagePath;
  }

  return `${baseUrl}${cleanImagePath}`;
};

const ManageCityInstruction: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCityIntro, setCurrentCityIntro] =
    useState<CityIntroduction | null>(null);
  const [cityIntros, setCityIntros] = useState<CityIntroduction[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [cities, setCities] = useState<City[]>([]);
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [selectedCity, setSelectedCity] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Hooks
  const getCityIntros = useGetCityIntroductionDetails();
  const addCityIntro = useAddCityIntroductionDetails();
  const updateCityIntro = useUpdateCityIntroductionDetails();
  const updateStatus = useCityIntroductionStatus();
  const imageUploadMutation = useCityIntroductionImageUpload(
    // onSuccess callback
    (filePath, url) => {
      const fullImageUrl = constructImageUrl(filePath);
      setUploadedImageUrl(fullImageUrl);
    },
    // onError callback
    (error) => {
      alert(error.message || "Failed to upload image. Please try again.");
    }
  );
  
  // Use the correct getCityByname hook with debounced search term
  const citiesQuery = getCityByname(debouncedSearchTerm);
  const getBookingTypes = useGetAllBookingTypes(); // Auto-fetches booking types

  const user = useAuth();

  // Fetch city introductions on component mount
  useEffect(() => {
    fetchCityIntros();
  }, []);

  const fetchCityIntros = () => {
    getCityIntros.mutate(
      {},
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            setCityIntros(data.data);
          }
        },
        onError: (error) => {
          console.error("Error fetching city introductions:", error);
        },
      }
    );
  };

  // Debounce search term - only search after 3 characters and 300ms delay
  useEffect(() => {
    if (citySearchTerm.length >= 3) {
      const handler = setTimeout(() => {
        setDebouncedSearchTerm(citySearchTerm);
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    } else {
      setCities([]);
      setDebouncedSearchTerm("");
    }
  }, [citySearchTerm]);

  // Update cities when debounced search term changes and query returns data
  useEffect(() => {
    if (debouncedSearchTerm && citiesQuery.data) {
      // Extract the cities data from the API response
      // The response should be an array of city objects
      const citiesData:any = citiesQuery.data;
      console.log("cities Data", citiesData.data);
      if (citiesData.data && Array.isArray(citiesData.data)) {
        setCities(citiesData.data);
      } else {
        setCities([]);
      }
    } else {
      setCities([]);
    }
  }, [debouncedSearchTerm, citiesQuery.data]);

  // Handle file upload when file is selected - IMMEDIATE UPLOAD
  const handleFileUpload = async (file: File) => {
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    // Create temporary preview URL for immediate display
    const tempUrl = URL.createObjectURL(file);
    setUploadedImageUrl(tempUrl);

    // Use the mutation to upload the file immediately
    imageUploadMutation.mutate(file, {
      onSuccess: (data) => {
        // The onSuccess callback in useCityIntroductionImageUpload will handle setting the proper URL
        // Revoke the temporary URL
        if (tempUrl.startsWith("blob:")) {
          URL.revokeObjectURL(tempUrl);
        }
      },
      onError: (error) => {
        // Revoke temporary URL on error
        if (tempUrl.startsWith("blob:")) {
          URL.revokeObjectURL(tempUrl);
        }
        setUploadedImageUrl("");
      },
    });
  };

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      // When closing modal, reset everything
      setUploadedImageUrl("");
      setCurrentCityIntro(null);
      setSelectedCity(null);
      setCitySearchTerm("");
      setDebouncedSearchTerm("");
      setCities([]);
    } else if (currentCityIntro) {
      // When opening modal for editing, set the selected city
      setSelectedCity({
        id: currentCityIntro.city_id.toString(),
        name: currentCityIntro.city_name,
      });
      setCitySearchTerm(currentCityIntro.city_name);
    }
  }, [isModalOpen, currentCityIntro]);

  // Transform booking types data for form options
  const bookingTypeOptions =
    getBookingTypes.data?.data?.map((bookingType: BookingType) => ({
      value: bookingType.id.toString(),
      label: bookingType.booking_type,
    })) || [];

  const formFields: any = [
    {
      id: "city_search",
      label: "City *",
      type: "searchselect",
      required: true,
      defaultValue: citySearchTerm,
      placeholder: "Type at least 3 characters to search for city...",
      options: cities.map((city) => ({
        value: city.city_id.toString(), // Use city_id as value
        label: `${city.city_name}, ${city.state_name}, ${city.country_code}`, // Show more context
      })),
      loading: citiesQuery.isLoading,
      onSearchChange: (value: string) => setCitySearchTerm(value),
      onOptionSelect: (value: string, label: string) => {
        const selectedCityData = cities.find(city => city.city_id.toString() === value);
        if (selectedCityData) {
          setSelectedCity({ 
            id: value, 
            name: selectedCityData.city_name 
          });
          setCitySearchTerm(selectedCityData.city_name);
        }
        setCities([]);
      },
      selectedValue: selectedCity
        ? { value: selectedCity.id, label: selectedCity.name }
        : null,
    },
    {
      id: "booking_type",
      label: "Booking Type *",
      type: "select",
      required: true,
      defaultValue: currentCityIntro ? currentCityIntro.booking_type : "",
      options: bookingTypeOptions,
      loading: getBookingTypes.isLoading,
    },
    {
      id: "title",
      label: "Title *",
      type: "text",
      required: true,
      placeholder: "Enter city introduction title",
      defaultValue: currentCityIntro ? currentCityIntro.title : "",
    },
    {
      id: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Enter city introduction description",
      defaultValue: currentCityIntro ? currentCityIntro.description : "",
    },
    {
      id: "image_path",
      label: "Thumb Image",
      type: "file",
      required: false,
      defaultValue: currentCityIntro ? currentCityIntro.image_path : "",
      accept: "image/*",
      loading: imageUploadMutation.isPending,
      onFileChange: handleFileUpload,
      currentImage: currentCityIntro?.image_path
        ? constructImageUrl(currentCityIntro.image_path)
        : "",
      uploadedImage: uploadedImageUrl,
    },
  ];

  const handleFormSubmit = async (formData: any) => {
    const loggedInUser = user?.user?.id;

    // Validate city selection
    if (!selectedCity) {
      alert("Please select a city");
      return;
    }

    try {
      // Extract just the path part from the uploaded image URL if it's a full URL
      let imagePath = "";
      if (uploadedImageUrl) {
        // If it's a newly uploaded image, get just the path part
        const url = new URL(uploadedImageUrl);
        imagePath = url.pathname;
      } else if (currentCityIntro?.image_path) {
        // If no new upload, use the existing path
        imagePath = currentCityIntro.image_path;
      }

      const payload = {
        city_id: selectedCity.id, // Use the selected city ID (city_id)
        booking_type: formData.booking_type,
        title: formData.title,
        description: formData.description,
        image_path: imagePath,
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentCityIntro) {
        // Update existing city introduction
        updateCityIntro.mutate(
          {
            ...payload,
            auto_id: currentCityIntro.id,
            status: currentCityIntro.status,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchCityIntros();
                setIsModalOpen(false);
                setCurrentCityIntro(null);
                setUploadedImageUrl("");
              }
            },
            onError: (error) => {
              alert("Failed to update city introduction. Please try again.");
            },
          }
        );
      } else {
        // Add new city introduction
        addCityIntro.mutate(
          {
            ...payload,
            status: 1,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchCityIntros();
                setIsModalOpen(false);
                setUploadedImageUrl("");
              }
            },
            onError: (error) => {
              alert("Failed to add city introduction. Please try again.");
            },
          }
        );
      }
    } catch (error) {
      alert("An error occurred. Please try again.");
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        status: 1,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchCityIntros();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        status: 0,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchCityIntros();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    if (
      confirm("Are you sure you want to delete selected city introductions?")
    ) {
      // Note: You might need to implement a delete mutation if available
      // For now, we'll just clear selection
      setSelectedRows([]);
    }
  };

  // Transform API data to match table structure
  const tableData = cityIntros.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    city: item.city_name,
    package_name: item.package_name,
    title: item.title,
    description: item.description,
    thumbImage: item.image_path,
    createdBy: item.created_by,
    status: item.status,
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    {
      key: "sno",
      header: "S.No",
      sortable: true,
    },
    {
      key: "city",
      header: "City",
      sortable: true,
    },
    {
      key: "package_name",
      header: "Booking Type",
      sortable: true,
    },
    {
      key: "title",
      header: "Title",
      sortable: true,
    },
    {
      key: "description",
      header: "Description",
      sortable: true,
    },
    {
      key: "thumbImage",
      header: "Image",
      sortable: false,
      render: (item: any) => {
        if (!item) {
          return (
            <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-xs text-gray-500">No Image</span>
            </div>
          );
        }

        const imageUrl = constructImageUrl(item);

        return (
          <div className="w-12 h-12 rounded overflow-hidden">
            <img
              src={imageUrl}
              alt="Thumb"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239CA3AF'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'%3E%3C/path%3E%3C/svg%3E";
              }}
            />
          </div>
        );
      },
    },
    {
      key: "createdBy",
      header: "Created By",
      sortable: true,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (item: any) => {
        return (
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                item === 1 ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm">
              {item === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentCityIntro(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (
                  confirm(
                    "Are you sure you want to delete this city introduction?"
                  )
                ) {
                  setSelectedRows([row.id]);
                  handleDelete();
                }
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentCityIntro(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
            disabled={getBookingTypes.isLoading}
          >
            {getBookingTypes.isLoading ? "Loading..." : "Add City Introduction"}
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={getCityIntros.isPending || imageUploadMutation.isPending}
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentCityIntro(null);
              setUploadedImageUrl("");
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentCityIntro
                    ? "Update City Introduction"
                    : "Add City Introduction"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentCityIntro(null);
                    setUploadedImageUrl("");
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields}
                  onSubmit={handleFormSubmit}
                  submitText={
                    currentCityIntro
                      ? "Update City Introduction"
                      : "Add City Introduction"
                  }
                  loading={
                    addCityIntro.isPending ||
                    updateCityIntro.isPending ||
                    imageUploadMutation.isPending
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCityInstruction;